import React, { createContext, useContext, useMemo, useRef, useState } from 'react';

export const ExternalOrdersContext = createContext();

/**
 * Filters of the external orders list, held above the routes so opening an order — or creating one —
 * and coming back to the list does not drop them. ClientContainer keeps this provider mounted for
 * every path of the module, `getProviders` matching on the path prefix, so the state lives exactly as
 * long as the user stays in the external orders module: leaving it drops everything, and a reload
 * drops the filters — the list params come back from the query string, which is theirs to hold.
 *
 * The tab, the pagination and the sort are kept in refs rather than in state: the query string
 * remains what the list renders from, and they only stand in as the values to restore when the list
 * is reached again on its bare path. Holding them in state would re-render the whole module for
 * params it does not read.
 */
export const ExternalOrdersProvider = ({ children }) => {
  const [search, setSearch] = useState('');

  // Store selection of the filters modal, seeded with the perimeter once it is known.
  const [selectedStores, setSelectedStores] = useState(null);
  // The perimeter that selection was seeded from, to tell a real perimeter change from a remount.
  const [storePerimeterKey, setStorePerimeterKey] = useState(null);

  const [filters, setFilters] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({});

  // Tab, pagination and sort the list was left on, and the same params of the tabs left behind.
  const listParamsRef = useRef(null);
  const paramsByTabRef = useRef({});

  const value = useMemo(
    () => ({
      search,
      setSearch,
      selectedStores,
      setSelectedStores,
      storePerimeterKey,
      setStorePerimeterKey,
      filters,
      setFilters,
      advancedFilters,
      setAdvancedFilters,
      appliedFilters,
      setAppliedFilters,
      listParamsRef,
      paramsByTabRef,
    }),
    [search, selectedStores, storePerimeterKey, filters, advancedFilters, appliedFilters],
  );

  return <ExternalOrdersContext.Provider value={value}>{children}</ExternalOrdersContext.Provider>;
};

/**
 * The provider sits above the routes rather than inside the list, so a consumer rendering without it
 * means the module was reached through a path `getProviders` does not map — a mistake that would
 * otherwise surface as a destructuring TypeError naming a field instead of the missing provider.
 *
 * @returns {Object} The external orders context value
 */
export const useExternalOrdersContext = () => {
  const context = useContext(ExternalOrdersContext);

  if (!context) {
    throw new Error('useExternalOrdersContext must be used within an ExternalOrdersProvider');
  }

  return context;
};
